import React, { ReactElement, useRef, useState } from "react";
import {
	Button,
	Content,
	DataObserveKey,
	Icon,
	IconElementProps,
	InlineText,
	Spinner,
	useFormattingLanguage,
	useUniqueId,
} from "@siteimprove/fancylib";
import { Popover } from "../../overlay/popover/popover";
import { TableToolbar } from "../../tables-and-lists/table-toolbar/table-toolbar";
import { InputField } from "../../forms-and-inputs/input-field/input-field";
import { useLabTranslations } from "../../../translations/translations";
import { toFormattedNumberString } from "../../text/formatted-number/formatted-number";
import scss from "./page-header-picker.scss";

export type PageHeaderPickerProps<TDto> = {
	/** Function to render the content and items of the picker. */
	contentItems: (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		firstFocusableRef: React.RefObject<any>,
		close: () => void,
		tableClassName?: string,
		selectButtonClassName?: string
	) => React.ReactNode;
	/** Items count of the current loaded items. */
	itemsCount: number;
	/** Total items count. */
	totalItems: number;
	/** Search options to filter picker items. */
	search: {
		/** Search query. */
		query: string;
		/** Search callback to filter picker items. */
		onSearch: (query: string) => void;
	};
	/** Load more callback. */
	onLoadMore: () => void;
	/** Popover button icon. */
	popoverButtonIcon: ReactElement<IconElementProps>;
	/** Function to stringify the selected item in the popover button. */
	selectedItemStringify: (selected: TDto) => string;
	/** Translations. */
	texts: {
		buttonContentNoItemSelected: string;
		showingXOfYItems: (showing: number, total: number) => string;
		loadMore?: (count: number) => string;
	};
	/** Selected item. */
	selectedItem?: TDto;
	/** Toolbar actions. */
	toolbarActions?: React.ReactNode;
	/** If the picker is loading. */
	loading?: boolean;
	/** Amount of items to take on Load more. */
	take?: number;
} & DataObserveKey;

export function PageHeaderPicker<TDto>(props: PageHeaderPickerProps<TDto>): JSX.Element {
	const {
		contentItems,
		itemsCount,
		totalItems,
		search: { query: searchQuery, onSearch },
		onLoadMore,
		take,
		popoverButtonIcon,
		selectedItem,
		toolbarActions,
		loading,
		texts,
	} = props;

	const selectedContent = selectedItem ? props.selectedItemStringify(selectedItem) : undefined;

	const [hasSearchQuery, setHasSearchQuery] = useState(false);

	return (
		<Popover
			popoverContent={(id, firstFocusableRef, openState) => (
				<div id={id}>
					<TableToolbar
						actions={toolbarActions}
						filter={
							<SearchInputFieldFilter
								query={searchQuery}
								onSearch={(query) => {
									onSearch(query);
									setHasSearchQuery(query.length > 0);
								}}
							/>
						}
					/>
					<div className={scss.containerItems}>
						<div className={scss.containerItemsCount} aria-live="polite" aria-atomic="true">
							<InlineText tone="subtle">
								{texts.showingXOfYItems(itemsCount, totalItems)}
							</InlineText>
							<div className={scss.loading}>
								{loading && <Spinner variant="dark" size="medium" appearanceDelay={0} />}
							</div>
						</div>
						{contentItems(
							firstFocusableRef,
							openState.close,
							scss.tableMaxHeight,
							scss.buttonSelectItem
						)}
						<LoadMore
							itemsCount={itemsCount}
							totalItems={totalItems}
							loadMoreTextCount={texts.loadMore}
							onLoadMore={onLoadMore}
							loading={loading}
							hasSearchQuery={hasSearchQuery}
							take={take}
						/>
					</div>
				</div>
			)}
			noMaxWidth
			buttonContent={
				<Content padding="none" flexDirection="row" alignItems="center" gap="xSmall">
					<Icon>{popoverButtonIcon}</Icon>
					<div className={scss.containerButton}>
						{selectedItem && selectedContent ? (
							<span
								className={scss.ellipsis}
								title={selectedContent.length > 18 ? selectedContent : undefined}
							>
								{selectedContent}
							</span>
						) : (
							<span className={scss.ellipsis}>{texts.buttonContentNoItemSelected}</span>
						)}
					</div>
				</Content>
			}
			buttonProps={{ variant: "borderless", size: "large" }}
			data-observe-key={props["data-observe-key"]}
		/>
	);
}

function SearchInputFieldFilter({
	query,
	onSearch,
}: {
	query: string;
	onSearch: (query: string) => void;
}): JSX.Element {
	const [value, setValue] = useDebounce(query, onSearch);

	const uniqueId = useUniqueId("search-input-field-filter");
	const i18n = useLabTranslations();

	return (
		<InputField
			aria-label={i18n.search}
			placeholder={i18n.search}
			name={uniqueId}
			value={value}
			onChange={setValue}
			type="search"
		/>
	);
}

function LoadMore({
	itemsCount,
	totalItems,
	loadMoreTextCount,
	onLoadMore,
	hasSearchQuery,
	loading,
	take = 20,
}: {
	itemsCount: number;
	totalItems: number;
	loadMoreTextCount?: (count: number) => string;
	onLoadMore: () => void;
	hasSearchQuery: boolean;
	loading?: boolean;
	take?: number;
}): JSX.Element {
	const i18n = useLabTranslations();
	const locale = useFormattingLanguage();

	const count = Math.min(totalItems - itemsCount, take);

	return totalItems > take && itemsCount < totalItems && !hasSearchQuery ? (
		<div className={scss.footer}>
			<Button onClick={onLoadMore} loading={loading}>
				<InlineText tone="subtle">
					{loadMoreTextCount
						? loadMoreTextCount(count)
						: i18n.loadXMore.replace(
								"[COUNT]",
								toFormattedNumberString({ format: "number", number: count, locale })
						  )}
				</InlineText>
			</Button>
		</div>
	) : (
		<></>
	);
}

function useDebounce<T>(
	initialValue: T,
	onChange: (v: T) => void,
	options: { wait?: number; invokeInitially?: boolean } = {}
): readonly [T, (v: T) => void] {
	const debounceFn = useDebounceRef(onChange, options.wait ?? 500);
	const [val, setVal] = useState(() => {
		if (options.invokeInitially) {
			onChange(initialValue);
		}
		return initialValue;
	});
	return [
		val,
		(v: T) => {
			setVal(v);
			debounceFn(v);
		},
	] as const;
}

function useDebounceRef(callback: (...args: any[]) => void, time = 500) {
	const interval = useRef(undefined as unknown);

	return (...args: any[]) => {
		clearTimeout(interval.current as number | undefined);
		interval.current = setTimeout(() => {
			callback(...args);
		}, time);
	};
}
